import React, { useContext, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Button, Icon } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { getCustomerWalletActions } from 'pages/Customers/helpers/utils'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BulkCapabilityResponse } from 'types/capabilities/capabilities'
import {
  CustomerWallet,
  CustomerWalletDepositDetails,
  NestedCustomerDetails,
  OrchestratedCustomerWallet,
} from 'types/customers'
import { ActionIds } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'

type WalletActionButtonsProps = {
  customer: NestedCustomerDetails
  customerWallet: CustomerWallet
  bulkCapabilities: BulkCapabilityResponse
  isFetchingCapabilities: boolean
  isFetchingWalletLegacyId: boolean
  setIsSheetOpen: React.Dispatch<React.SetStateAction<boolean>>
  cleanupUnifiedPayoutsFlow: () => void
}

export const WalletActionButtons: React.FC<WalletActionButtonsProps> = ({
  customer,
  customerWallet,
  bulkCapabilities,
  isFetchingCapabilities,
  isFetchingWalletLegacyId,
  setIsSheetOpen,
  cleanupUnifiedPayoutsFlow,
}) => {
  const { t } = useTranslation()

  const { accountCapabilities } = useAccountsContext()
  const { viewOnly: isViewOnlyRole } = useProfileContext() || {}
  const [, setWalletAction] = useContext(WalletActionContext)
  const customerModel = useAtomValue(customerModelAtom)

  const orchestratedCustomerWallet: OrchestratedCustomerWallet = useMemo(() => {
    const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
      getFiatWalletActionSupport({
        walletProperties: {
          currency: customerWallet?.balance?.currencyCode,
          lsid: customerWallet?.id,
        },
        bulkCapabilities,
      })

    return {
      ...customerWallet,
      supportsDeposits: supportsDeposits,
      supportsWithdrawals: supportsWithdrawals,
      supportsThirdParty: supportsPay,
      supportsInternalTransfers: supportsInternalTransfers,
    }
  }, [bulkCapabilities, customerWallet])

  const handleOpenFiatDepositSheet = () => {
    if (orchestratedCustomerWallet) {
      const customerWalletDepositDetails: CustomerWalletDepositDetails = {
        wallet: orchestratedCustomerWallet,
        customer: customer,
      }
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWalletDepositDetails,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = () => {
    if (orchestratedCustomerWallet) {
      setWalletAction({
        actionId: ActionIds.MOVE,
        header: t('customers.moveFunds'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          sourceWalletType: WalletTransferType?.CUSTOMER,
          sourceCustomerWallet: orchestratedCustomerWallet,
          customerModel,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenSendFiatSheet = () => {
    if (orchestratedCustomerWallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: orchestratedCustomerWallet?.id,
        meta: {
          customerWallet: orchestratedCustomerWallet,
        },
      })

      setIsSheetOpen(true)
    }
  }

  const handleOpenSendCryptoSheet = () => {
    if (orchestratedCustomerWallet) {
      setWalletAction({
        actionId: ActionIds?.SEND,
        header: t('wallets.send.contactStepHeader'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWallet: orchestratedCustomerWallet,
        },
      })
      setIsSheetOpen(true)
    }
  }

  const walletActionButtons = useMemo(() => {
    if (isFetchingCapabilities || isFetchingWalletLegacyId) return null

    const {
      isCryptoSendOptionEnabled,
      isUnifiedPayoutOptionEnabled,
      isFiatMoveOptionEnabled,
      isCryptoMoveOptionEnabled,
      isFiatDepositOptionEnabled,
    } = getCustomerWalletActions({
      accountCapabilities,
      customerWallet: orchestratedCustomerWallet,
      isViewOnlyRole,
      customerModel,
    })

    return [
      ...(isUnifiedPayoutOptionEnabled
        ? [
            <Button
              key="sendFiat"
              testId="sendFiat"
              onClick={handleOpenSendFiatSheet}
              iconElement={<Icon name="CoinArrowOutCustom" />}
              disabled={isViewOnlyRole}
            >
              {t('wallets.fiatSend.sendFiat')}
            </Button>,
          ]
        : []),
      ...(isCryptoSendOptionEnabled
        ? [
            <Button
              key="sendCrypto"
              testId="sendCrypto"
              onClick={handleOpenSendCryptoSheet}
              disabled={isViewOnlyRole}
              iconElement={<Icon name="ArrowUpRight" />}
            >
              {t('payments.sendCrypto')}
            </Button>,
          ]
        : []),
      ...(isFiatDepositOptionEnabled
        ? [
            <Button
              key="depositButton"
              onClick={handleOpenFiatDepositSheet}
              iconElement={<Icon name="ArrowDownLeft" />}
              disabled={isViewOnlyRole}
            >
              {t('deposit')}
            </Button>,
          ]
        : []),
      ...(isFiatMoveOptionEnabled || isCryptoMoveOptionEnabled
        ? [
            <Button
              key="moveButton"
              onClick={handleOpenMoveSheet}
              iconElement={<Icon name="ArrowsLeftRight" />}
              disabled={isViewOnlyRole}
            >
              {t('customers.move')}
            </Button>,
          ]
        : []),
    ]
  }, [
    bulkCapabilities,
    isFetchingCapabilities,
    isFetchingWalletLegacyId,
    isViewOnlyRole,
    customerModel,
    orchestratedCustomerWallet,
  ])

  return <>{walletActionButtons}</>
}
